import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchAllAdminData } from '../../store/admin';
import { fetchMoves } from '../../store/moves';
import { generateReport, exportToExcel } from '../../utils/printReport';
import { setSelectedAccount } from '../../store/ui';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [activeTab, setActiveTab] = useState('trailers');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [timeRange, setTimeRange] = useState('daily');
  const searchQuery = useSelector((state) => state.ui.searchQuery);
  const [locationHistoryData, setLocationHistoryData] = useState({});
  const [isLoadingLocationData, setIsLoadingLocationData] = useState(false);
  const [truckLocationHistoryData, setTruckLocationHistoryData] = useState({});

  // Add filter states for trailers
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedLoadType, setSelectedLoadType] = useState('all');
  const [timeSort, setTimeSort] = useState('asc');

  // Add filter states for trucks
  const [selectedTruckLocation, setSelectedTruckLocation] = useState('all');
  const [selectedTruckTag, setSelectedTruckTag] = useState('all');
  const [selectedTruckAssignedUser, setSelectedTruckAssignedUser] = useState('all');

  // Add filter states for users
  const [selectedUserRole, setSelectedUserRole] = useState('all');
  const [selectedUserStatus, setSelectedUserStatus] = useState('all');

  // Get current user to check permissions
  const currentUser = useSelector((state) => state.session?.user);
  const isManagerOrAbove = currentUser && (currentUser.is_manager || currentUser.is_account_manager || currentUser.is_super_manager || currentUser.is_admin);

  const companies = useSelector((state) => Array.isArray(state.companies) ? state.companies : Object.values(state.companies));
  const accounts = useSelector((state) => Array.isArray(state.accounts) ? state.accounts : Object.values(state.accounts));
  const trucks = useSelector((state) => Array.isArray(state.trucks) ? state.trucks : Object.values(state.trucks));
  const trailers = useSelector((state) => Array.isArray(state.trailers) ? state.trailers : Object.values(state.trailers));
  const buildings = useSelector((state) => Array.isArray(state.buildings) ? state.buildings : Object.values(state.buildings));
  const yards = useSelector((state) => Array.isArray(state.yards) ? state.yards : Object.values(state.yards));
  const users = useSelector((state) => Array.isArray(state.users) ? state.users : Object.values(state.users));
  const moves = useSelector((state) => state.moves?.list || []);

  useEffect(() => {
    dispatch(fetchAllAdminData());
    dispatch(fetchMoves());
  }, [dispatch]);

  useEffect(() => {
    if (companies.length && !selectedCompanyId) {
      const firstCompany = companies[0];
      setSelectedCompanyId(firstCompany.id);

      const firstCompanyAccount = accounts.find(account => account.company_id === firstCompany.id);
      if (firstCompanyAccount) {
        setSelectedAccountId(firstCompanyAccount.id);
        dispatch(setSelectedAccount(firstCompanyAccount.id));
      } else {
        console.log('No accounts found for the first company');
      }
    }
  }, [companies, accounts, selectedCompanyId, dispatch]);

  // Get current location duration for a trailer
  const getCurrentLocationDuration = (trailerId) => {
    if (!trailerId) return 'N/A';

    const locationHistory = locationHistoryData[trailerId];
    if (!locationHistory || locationHistory.length === 0) {
      return 'N/A';
    }

    const currentLocation = locationHistory[0];
    if (!currentLocation) return 'N/A';

    // If there's an exit time, the trailer is no longer at this location
    if (currentLocation.exit_time) return 'Moved';

    // Return the formatted duration if available
    if (currentLocation.current_duration_formatted) {
      // Replace the formatted duration with abbreviated units
      const formattedDuration = currentLocation.current_duration_formatted;
      return formattedDuration
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' mins', 'm')
        .replace(' min', 'm');
    }

    // If we have an entry time but no formatted duration, calculate a simple duration
    if (currentLocation.entry_time) {
      const entryTime = new Date(currentLocation.entry_time);
      const now = new Date();
      const diffMs = now - entryTime;

      // Calculate days, hours, minutes
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      // Format with abbreviated units
      if (diffDays > 0) {
        return `${diffDays}d ${diffHrs}h`;
      } else if (diffHrs > 0) {
        return `${diffHrs}h ${diffMins}m`;
      } else {
        return `${diffMins}m`;
      }
    }

    return 'Just arrived';
  };

  // Get current location duration for a truck
  const getTruckLocationDuration = (truckId) => {
    if (!truckId) return 'N/A';

    const locationHistory = truckLocationHistoryData[truckId];
    if (!locationHistory || locationHistory.length === 0) {
      return 'N/A';
    }

    const currentLocation = locationHistory[0];
    if (!currentLocation) return 'N/A';

    // If there's an exit time, the truck is no longer at this location
    if (currentLocation.exit_time) return 'Moved';

    // Return the formatted duration if available
    if (currentLocation.current_duration_formatted) {
      // Replace the formatted duration with abbreviated units
      const formattedDuration = currentLocation.current_duration_formatted;
      return formattedDuration
        .replace(' days', 'd')
        .replace(' day', 'd')
        .replace(' hours', 'h')
        .replace(' hour', 'h')
        .replace(' minutes', 'm')
        .replace(' minute', 'm')
        .replace(' mins', 'm')
        .replace(' min', 'm');
    }

    // If we have an entry time but no formatted duration, calculate a simple duration
    if (currentLocation.entry_time) {
      const entryTime = new Date(currentLocation.entry_time);
      const now = new Date();
      const diffMs = now - entryTime;

      // Calculate days, hours, minutes
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHrs = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      // Format with abbreviated units
      if (diffDays > 0) {
        return `${diffDays}d ${diffHrs}h`;
      } else if (diffHrs > 0) {
        return `${diffHrs}h ${diffMins}m`;
      } else {
        return `${diffMins}m`;
      }
    }

    return 'Just arrived';
  };

  // Fetch trailer location history data
  useEffect(() => {
    let mounted = true;

    const fetchLocationHistoryData = async () => {
      if (!selectedAccountId) return;

      setIsLoadingLocationData(true);
      try {
        // Fetch all location histories for the account at once
        const response = await fetch(`/api/accounts/${selectedAccountId}/trailers-location-history`);
        if (response.ok && mounted) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            setLocationHistoryData(data);
          }
        }
      } catch (error) {
        console.error('Error fetching trailer location history data:', error);
      } finally {
        if (mounted) {
          setIsLoadingLocationData(false);
        }
      }
    };

    fetchLocationHistoryData();

    return () => {
      mounted = false;
    };
  }, [selectedAccountId]);

  // Fetch truck location history data
  useEffect(() => {
    let mounted = true;

    const fetchTruckLocationHistoryData = async () => {
      if (!selectedAccountId) return;

      setIsLoadingLocationData(true);
      try {
        // Fetch all location histories for the account at once
        const response = await fetch(`/api/accounts/${selectedAccountId}/trucks-location-history`);
        if (response.ok && mounted) {
          const data = await response.json();
          if (data && Object.keys(data).length > 0) {
            setTruckLocationHistoryData(data);
          }
        }
      } catch (error) {
        console.error('Error fetching truck location history data:', error);
      } finally {
        if (mounted) {
          setIsLoadingLocationData(false);
        }
      }
    };

    fetchTruckLocationHistoryData();

    return () => {
      mounted = false;
    };
  }, [selectedAccountId]);

  useEffect(() => {
    if (selectedCompanyId) {
      const companyAccounts = accounts.filter(account => account.company_id === selectedCompanyId);

      if (companyAccounts.length > 0) {
        // Check if the currently selected account belongs to the selected company
        const currentAccountBelongsToCompany = companyAccounts.some(account => account.id === selectedAccountId);

        if (!currentAccountBelongsToCompany) {
          setSelectedAccountId(companyAccounts[0].id);
        }
      } else {
        console.log(`No accounts found for company ${selectedCompanyId}`);
        setSelectedAccountId(null); // Reset the account selection if there are no accounts
      }
    }
  }, [selectedCompanyId, accounts, selectedAccountId]);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px
      setShowScrollButton(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to get status priority for sorting
  const getStatusPriority = (item) => {
    if (item.red_tagged) return 1; // Highest priority
    if (item.yellow_tagged) return 2;
    if (item.in_shop) return 3;
    return 4; // Available - lowest priority
  };

  // Add this function to filter moves based on time range
  const getFilteredMoves = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return moves.filter(move => {
      // First filter by selected account
      if (selectedAccountId && move.account_id !== selectedAccountId) {
        return false;
      }

      const moveDate = new Date(move.request_time);

      switch (timeRange) {
        case 'daily':
          return moveDate.toDateString() === today.toDateString();
        case 'weekly': {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return moveDate >= sevenDaysAgo;
        }
        case 'monthly': {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return moveDate >= thirtyDaysAgo;
        }
        default:
          return true;
      }
    });
  };

  // Add this function to calculate average completion time
  const calculateAverageTime = (filteredMoves) => {
    const completedMoves = filteredMoves.filter(move => move.finish_time);
    if (completedMoves.length === 0) return 'N/A';

    const totalMinutes = completedMoves.reduce((acc, move) => {
      const start = new Date(move.request_time);
      const end = new Date(move.finish_time);
      return acc + (end - start) / (1000 * 60);
    }, 0);

    return Math.round(totalMinutes / completedMoves.length) + ' min';
  };

  // Add this function to calculate average time at locations
  const calculateAverageTimeAtLocations = () => {
    if (!locationHistoryData.length) return 'No data';

    let totalDurationSeconds = 0;
    let recordCount = 0;

    locationHistoryData.forEach(trailer => {
      trailer.history.forEach(record => {
        if (record.duration_seconds) {
          totalDurationSeconds += record.duration_seconds;
          recordCount++;
        }
      });
    });

    if (recordCount === 0) return 'No data';

    const avgSeconds = totalDurationSeconds / recordCount;
    const hours = Math.floor(avgSeconds / 3600);
    const minutes = Math.floor((avgSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes} minutes`;
    }
  };

  // Function to find most active location
  const findMostActiveLocation = () => {
    if (!locationHistoryData.length) return 'No data';

    const locationCounts = {};

    // Count occurrences of each location in the history data
    locationHistoryData.forEach(trailer => {
      trailer.history.forEach(record => {
        if (record.location) {
          locationCounts[record.location] = (locationCounts[record.location] || 0) + 1;
        }
      });
    });

    if (Object.keys(locationCounts).length === 0) return 'No data';

    // Find the location with the highest count
    let mostActiveLocation = '';
    let highestCount = 0;

    Object.entries(locationCounts).forEach(([location, count]) => {
      if (count > highestCount) {
        mostActiveLocation = location;
        highestCount = count;
      }
    });

    return mostActiveLocation;
  };

  // Function to count idle trailers (trailers that haven't moved in 7+ days)
  const countIdleTrailers = () => {
    if (!locationHistoryData.length) return 0;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    let idleCount = 0;

    locationHistoryData.forEach(trailer => {
      // If there's location history
      if (trailer.history.length > 0) {
        // Get the most recent record (the records are sorted by entry_time in descending order)
        const mostRecentRecord = trailer.history[0];

        // If there's no exit_time, the trailer is still at this location
        if (!mostRecentRecord.exit_time) {
          const entryDate = new Date(mostRecentRecord.entry_time);

          // If the entry date is more than 7 days ago, the trailer is idle
          if (entryDate < sevenDaysAgo) {
            idleCount++;
          }
        }
      }
    });

    return idleCount;
  };

  // Function to get popular move routes from move data
  const getPopularMoveRoutes = () => {
    const filteredMoves = getFilteredMoves();
    const routeCounts = {};

    filteredMoves.forEach(move => {
      if (move.move_details?.moves && move.finish_time) {
        move.move_details.moves.forEach(moveStep => {
          const route = `${moveStep.from} → ${moveStep.to}`;
          routeCounts[route] = (routeCounts[route] || 0) + 1;
        });
      }
    });

    // Get top 5 routes
    return Object.entries(routeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  };

  // Function to get location distribution data
  const getLocationDistribution = () => {
    const locationCounts = {};

    // Filter trailers by selected account and count current locations
    const accountTrailers = trailers.filter(trailer =>
      selectedAccountId ? trailer.account_id === selectedAccountId : true
    );

    accountTrailers.forEach(trailer => {
      const location = trailer.trailer_location || 'Unknown';
      locationCounts[location] = (locationCounts[location] || 0) + 1;
    });

    // Get top 5 locations
    return Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Generate unique location options from actual trailer locations
  const generateLocationOptions = () => {
    if (!selectedAccountId) return [];

    // Get unique building names from account buildings
    const buildingNames = buildings
      .filter(building => building.account_id === selectedAccountId)
      .map(b => b.building_name)
      .filter(Boolean);

    // Get unique yard names from account yards
    const yardNames = yards
      .filter(yard => yard.account_id === selectedAccountId)
      .map(y => y.name)
      .filter(Boolean);

    // Combine building and yard names, remove duplicates, and sort
    return [...new Set([...buildingNames, ...yardNames])].sort();
  };

  // Generate unique truck location options from actual truck locations
  const generateTruckLocationOptions = () => {
    if (!selectedAccountId) return [];

    // Extract unique locations from ALL trucks for this account, not just filtered ones
    const allAccountTrucks = trucks.filter(truck => truck.account_id === selectedAccountId);

    // Extract unique locations that are actually used by trucks
    const truckLocations = allAccountTrucks
      .map(truck => truck.truck_location)
      .filter(Boolean); // Remove undefined/null/empty values

    // Create a unique set of locations
    const uniqueLocations = [...new Set(truckLocations)];

    // Sort locations alphabetically
    return uniqueLocations.sort();
  };

  // Generate unique user role options
  const generateUserRoleOptions = () => {
    if (!selectedAccountId) return [];

    // Make sure we work with all users for this account, not just filtered ones
    const allAccountUsers = users.filter(user =>
      user.account_id === selectedAccountId ||
      (user.is_admin && user.account_id === null)
    );

    const roles = [
      { value: 'super_manager', label: 'Super Manager' },
      { value: 'account_manager', label: 'Account Manager' },
      { value: 'manager', label: 'Manager' },
      { value: 'admin', label: 'Admin' },
      { value: 'security', label: 'Security' },
      { value: 'mechanic', label: 'Mechanic' },
      { value: 'road_driver', label: 'Road Driver' },
      { value: 'yard_driver', label: 'Yard Driver' }
    ];

    // Only include roles that exist in accountUsers
    return roles.filter(role =>
      allAccountUsers.some(user => user[`is_${role.value}`])
    );
  };

  // Generate unique truck assigned users options
  const generateTruckAssignedUsersOptions = () => {
    if (!selectedAccountId) return [];

    // Get all trucks for the selected account (not just filtered ones)
    const allAccountTrucks = trucks.filter(truck => truck.account_id === selectedAccountId);

    // Get all user IDs that are assigned to trucks
    const assignedUserIds = allAccountTrucks
      .map(truck => truck.assigned_user_id)
      .filter(Boolean); // Remove undefined/null values

    // Get unique user IDs
    const uniqueUserIds = [...new Set(assignedUserIds)];

    // Find the actual users from the users array
    const assignedUsers = uniqueUserIds
      .map(userId => users.find(user => user.id === userId))
      .filter(Boolean); // Remove any not found

    // Sort by alpha code
    return assignedUsers.sort((a, b) =>
      a.alpha_code?.localeCompare(b.alpha_code || '') || 0
    );
  };

  // Reset filters when changing tabs
  useEffect(() => {
    setSelectedLocation('all');
    setSelectedTag('all');
    setSelectedLoadType('all');
    setTimeSort('asc');
    setSelectedTruckLocation('all');
    setSelectedTruckTag('all');
    setSelectedTruckAssignedUser('all');
    setSelectedUserRole('all');
    setSelectedUserStatus('all');
  }, [activeTab]);

  // Initialize account data collections
  let accountTrailers = [];
  let accountTrucks = [];
  let accountUsers = [];
  let accountYards = [];
  let accountBuildings = [];

  if (selectedAccountId) {
    // Base data filtering - get all data for the selected account
    const baseTrailers = trailers.filter(trailer => trailer.account_id === selectedAccountId);
    const baseTrucks = trucks.filter(truck => truck.account_id === selectedAccountId);
    const baseUsers = users.filter(user =>
      user.account_id === selectedAccountId ||
      (user.is_admin && user.account_id === null)
    );
    const baseYards = yards.filter(yard => yard.account_id === selectedAccountId);
    const baseBuildings = buildings.filter(building => building.account_id === selectedAccountId);

    // Apply search query filter
    if (searchQuery?.trim()) {
      const lowerQuery = searchQuery.trim().toLowerCase();

      // Filter trailers by search
      accountTrailers = baseTrailers.filter(trailer =>
        (trailer.trailer_location?.toLowerCase()?.includes(lowerQuery) || false) ||
        (trailer.trailer_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
        (trailer.type?.toLowerCase()?.includes(lowerQuery) || false) ||
        (trailer.license_plate?.toLowerCase()?.includes(lowerQuery) || false) ||
        (trailer.company_owner?.toLowerCase()?.includes(lowerQuery) || false) ||
        (trailer.load_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false)
      );

      // Filter trucks by search
      accountTrucks = baseTrucks.filter(truck =>
        (truck.truck_location?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.truck_number?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.type?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.make?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.model?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.license_plate?.toLowerCase()?.includes(lowerQuery) || false) ||
        (truck.vin?.toLowerCase()?.includes(lowerQuery) || false)
      );

      // Filter users by search
      accountUsers = baseUsers.filter(user =>
        (user.alpha_code?.toLowerCase()?.includes(lowerQuery) || false) ||
        (user.email?.toLowerCase()?.includes(lowerQuery) || false) ||
        (user.role?.toLowerCase()?.includes(lowerQuery) || false)
      );

      // Filter yards by search
      accountYards = baseYards.filter(yard =>
        (yard.name?.toLowerCase()?.includes(lowerQuery) || false) ||
        (yard.address?.toLowerCase()?.includes(lowerQuery) || false) ||
        (yard.city?.toLowerCase()?.includes(lowerQuery) || false) ||
        (yard.state?.toLowerCase()?.includes(lowerQuery) || false) ||
        (yard.zip_code?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
        (yard.description?.toLowerCase()?.includes(lowerQuery) || false)
      );

      // Filter buildings by search
      accountBuildings = baseBuildings.filter(building =>
        (building.building_name?.toLowerCase()?.includes(lowerQuery) || false) ||
        (building.address?.toLowerCase()?.includes(lowerQuery) || false) ||
        (building.city?.toLowerCase()?.includes(lowerQuery) || false) ||
        (building.state?.toLowerCase()?.includes(lowerQuery) || false) ||
        (building.zip_code?.toString()?.toLowerCase()?.includes(lowerQuery) || false) ||
        (building.description?.toLowerCase()?.includes(lowerQuery) || false)
      );
    } else {
      // If no search query, use the base collections
      accountTrailers = [...baseTrailers];
      accountTrucks = [...baseTrucks];
      accountUsers = [...baseUsers];
      accountYards = [...baseYards];
      accountBuildings = [...baseBuildings];
    }

    // Apply tab-specific filters
    if (activeTab === 'trailers') {
      // Apply location filter
      if (selectedLocation !== 'all') {
        accountTrailers = accountTrailers.filter(trailer => {
          const location = trailer.trailer_location?.toLowerCase() || '';
          if (selectedLocation === 'road') return location.includes('road');
          if (selectedLocation === 'shop') return location.includes('shop');
          if (selectedLocation === 'yard') return location.includes('yard');
          // For custom locations, use includes instead of exact match
          return location.includes(selectedLocation.toLowerCase());
        });
      }

      // Apply tag filter
      if (selectedTag !== 'all') {
        accountTrailers = accountTrailers.filter(trailer => {
          if (selectedTag === 'red') return trailer.red_tagged;
          if (selectedTag === 'yellow') return trailer.yellow_tagged;
          if (selectedTag === 'available') return !trailer.red_tagged && !trailer.yellow_tagged;
          return true;
        });
      }

      // Apply load type filter
      if (selectedLoadType !== 'all') {
        accountTrailers = accountTrailers.filter(trailer => {
          if (selectedLoadType === 'loaded') return trailer.is_loaded;
          if (selectedLoadType === 'empty') return !trailer.is_loaded && !trailer.is_tray;
          if (selectedLoadType === 'trays') return trailer.is_tray;
          return true;
        });
      }

      // Apply time sorting - Add this code
      if (timeSort && locationHistoryData) {
        accountTrailers = [...accountTrailers].sort((a, b) => {
          // Get the current location history entries for both trailers
          const aHistory = locationHistoryData[a.id]?.[0];
          const bHistory = locationHistoryData[b.id]?.[0];

          // Get entry times or default to 0 (for missing data)
          const aEntryTime = aHistory?.entry_time ? new Date(aHistory.entry_time).getTime() : 0;
          const bEntryTime = bHistory?.entry_time ? new Date(bHistory.entry_time).getTime() : 0;

          // Sort based on timeSort direction
          if (timeSort === 'asc') {
            return bEntryTime - aEntryTime; // Newest first
          } else {
            return aEntryTime - bEntryTime; // Oldest first
          }
        });
      }
    } else if (activeTab === 'trucks') {
      // Apply location filter
      if (selectedTruckLocation !== 'all') {
        accountTrucks = accountTrucks.filter(truck => {
          const location = truck.truck_location?.toLowerCase() || '';
          if (selectedTruckLocation === 'road') return location.includes('road');
          if (selectedTruckLocation === 'shop') return location.includes('shop');
          // For custom locations, use includes instead of exact match
          return location.includes(selectedTruckLocation.toLowerCase());
        });
      }

      // Apply tag filter
      if (selectedTruckTag !== 'all') {
        accountTrucks = accountTrucks.filter(truck => {
          if (selectedTruckTag === 'red') return truck.red_tagged;
          if (selectedTruckTag === 'yellow') return truck.yellow_tagged;
          if (selectedTruckTag === 'available') return !truck.red_tagged && !truck.yellow_tagged;
          return true;
        });
      }

      // Apply assigned user filter
      if (selectedTruckAssignedUser !== 'all') {
        accountTrucks = accountTrucks.filter(truck => {
          if (selectedTruckAssignedUser === 'unassigned') return !truck.assigned_user_id;
          return truck.assigned_user_id?.toString() === selectedTruckAssignedUser;
        });
      }

      // Apply time sorting for trucks
      if (timeSort && truckLocationHistoryData) {
        accountTrucks = [...accountTrucks].sort((a, b) => {
          // Get the current location history entries for both trucks
          const aHistory = truckLocationHistoryData[a.id]?.[0];
          const bHistory = truckLocationHistoryData[b.id]?.[0];

          // Get entry times or default to 0 (for missing data)
          const aEntryTime = aHistory?.entry_time ? new Date(aHistory.entry_time).getTime() : 0;
          const bEntryTime = bHistory?.entry_time ? new Date(bHistory.entry_time).getTime() : 0;

          // Sort based on timeSort direction
          if (timeSort === 'asc') {
            return bEntryTime - aEntryTime; // Newest first
          } else {
            return aEntryTime - bEntryTime; // Oldest first
          }
        });
      }
    } else if (activeTab === 'users') {
      // Apply role filter
      if (selectedUserRole !== 'all') {
        accountUsers = accountUsers.filter(user => user[`is_${selectedUserRole}`]);
      }

      // Apply status filter
      if (selectedUserStatus !== 'all') {
        accountUsers = accountUsers.filter(user =>
          selectedUserStatus === 'active' ? user.is_active : !user.is_active
        );
      }
    }

    // Apply sorting if needed
    if (sortField) {
      if (activeTab === 'trailers') {
        accountTrailers = [...accountTrailers].sort((a, b) => {
          // Special handling for status sorting
          if (sortField === 'status') {
            const statusA = getStatusPriority(a);
            const statusB = getStatusPriority(b);

            return sortDirection === 'asc'
              ? statusA - statusB
              : statusB - statusA;
          }

          // Regular field sorting
          let compareA = a[sortField] || '';
          let compareB = b[sortField] || '';

          if (sortDirection === 'asc') {
            return compareA.toString().localeCompare(compareB.toString());
          } else {
            return compareB.toString().localeCompare(compareA.toString());
          }
        });
      } else if (activeTab === 'trucks') {
        accountTrucks = [...accountTrucks].sort((a, b) => {
          // Special handling for status sorting
          if (sortField === 'status') {
            const statusA = getStatusPriority(a);
            const statusB = getStatusPriority(b);

            return sortDirection === 'asc'
              ? statusA - statusB
              : statusB - statusA;
          }

          // Regular field sorting
          let compareA = a[sortField] || '';
          let compareB = b[sortField] || '';
          if (sortDirection === 'asc') {
            return compareA.toString().localeCompare(compareB.toString());
          } else {
            return compareB.toString().localeCompare(compareA.toString());
          }
        });
      } else if (activeTab === 'users') {
        accountUsers = [...accountUsers].sort((a, b) => {
          // Special handling for status sorting
          if (sortField === 'is_active') {
            return sortDirection === 'asc'
              ? (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1)
              : (a.is_active === b.is_active ? 0 : a.is_active ? 1 : -1);
          }

          // Regular field sorting
          let compareA = a[sortField] || '';
          let compareB = b[sortField] || '';

          if (sortDirection === 'asc') {
            return compareA.toString().localeCompare(compareB.toString());
          } else {
            return compareB.toString().localeCompare(compareA.toString());
          }
        });
      } else if (activeTab === 'yards') {
        accountYards = [...accountYards].sort((a, b) => {
          let compareA = a[sortField] || '';
          let compareB = b[sortField] || '';

          if (sortDirection === 'asc') {
            return compareA.toString().localeCompare(compareB.toString());
          } else {
            return compareB.toString().localeCompare(compareA.toString());
          }
        });
      } else if (activeTab === 'buildings') {
        accountBuildings = [...accountBuildings].sort((a, b) => {
          let compareA = a[sortField] || '';
          let compareB = b[sortField] || '';

          if (sortDirection === 'asc') {
            return compareA.toString().localeCompare(compareB.toString());
          } else {
            return compareB.toString().localeCompare(compareA.toString());
          }
        });
      }
    }
  }

  if (!companies.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const companyAccounts = accounts.filter(account => account.company_id === selectedCompanyId);

  // Add user statistics
  // const userStats = {
  //   total: accountUsers.length,
  //   active: accountUsers.filter(user => user.is_active).length,
  //   inactive: accountUsers.filter(user => !user.is_active).length
  // };

  const getStatusColor = (item) => {
    if (item.red_tagged) return 'bg-red-100 text-red-800';
    if (item.yellow_tagged) return 'bg-yellow-100 text-yellow-800';
    if (item.in_shop) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (item) => {
    if (item.red_tagged) return 'Red Tagged';
    if (item.yellow_tagged) return 'Yellow Tagged';
    if (item.in_shop) return 'Shop';
    return 'Available';
  };

  // SortableHeader component
  const SortableHeader = ({ field, label }) => (
    <div
      onClick={() => handleSort(field)}
      className="flex items-center justify-center cursor-pointer hover:bg-blue-200 p-2 rounded"
    >
      {label}
      {sortField === field && (
        <span className="ml-1">
          {sortDirection === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="text-5xl font-semibold mb-6 bg-gradient-to-r from-blue-800 via-red-700 to-orange-500 text-transparent bg-clip-text">
        Admin Dashboard
      </div>

      {/* Company Selector */}
      <div className="w-1/2 mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <span className="text-4xl font-bold">Company:</span>
          <select id="company-selector"
            value={selectedCompanyId || ''}
            onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            className="flex-1 p-4 border border-gray-300 rounded-lg shadow-sm text-3xl"
          >
            {companies.map(company => (
              <option id={company.id} key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCompanyId && (
        <div className="grid grid-cols-1 gap-8 mb-8">
          {/* Accounts List */}
          <div className="bg-white rounded-lg shadow p-8">
            <div className="flex">
              <h2 className="text-4xl font-bold mb-6 mr-3">
                Account(s) under
              </h2>
              <div className="text-4xl font-bold mb-6 text-blue-500">
                {companies.find(c => c.id === selectedCompanyId)?.company_name || ''} :
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companyAccounts.map(account => (
                <div
                  key={account.id}
                  onClick={() => {
                    setSelectedAccountId(account.id);
                    dispatch(setSelectedAccount(account.id));
                  }}
                  className={`border-2 rounded-lg p-6 cursor-pointer transition-all duration-200 ${
                    selectedAccountId === account.id
                      ? 'border-blue-500 shadow-xl bg-blue-100'
                      : 'border-gray-200 hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]'
                  }`}
                >
                  <h3 className="text-3xl font-semibold mb-3">{account.account_name}</h3>
                  <p className="text-2xl text-gray-600">{account.location}</p>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <QuickStat
                      label="Trailers"
                      value={trailers.filter(t => t.account_id === account.id).length}
                      color="green"
                    />
                    <QuickStat
                      label="Trucks"
                      value={trucks.filter(t => t.account_id === account.id).length}
                      color="blue"
                    />
                    <QuickStat
                      label="Users"
                      value={users.filter(u => u.account_id === account.id).length}
                      color="purple"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Moves Summary Section */}
      <div className="relative bg-white rounded-lg shadow p-8 mb-8 z-10">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-4xl font-bold">Moves Summary</h2>
            {selectedAccountId && (
              <p className="text-gray-600 text-xl mt-1">
                Showing data for {accounts.find(a => a.id === selectedAccountId)?.account_name || 'selected account'}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="daily"
                type="radio"
                className="hidden"
                value="daily"
                checked={timeRange === 'daily'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Today</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'weekly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="weekly"
                type="radio"
                className="hidden"
                value="weekly"
                checked={timeRange === 'weekly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 7 Days</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="monthly"
                type="radio"
                className="hidden"
                value="monthly"
                checked={timeRange === 'monthly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 30 Days</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 min-h-[8rem]">
          {/* Get filtered moves once for all tiles */}
          {(() => {
            const filteredMoves = getFilteredMoves();
            const completedMoves = filteredMoves.filter(move => move.finish_time && !move.is_cancel);
            const successRate = filteredMoves.length > 0
              ? Math.round((completedMoves.length / filteredMoves.length) * 100)
              : 0;

            return (
              <>
                <div className="flex flex-col bg-blue-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Total Moves</h3>
                  <p className="text-4xl font-bold">{filteredMoves.length}</p>
                </div>
                <div className="flex flex-col bg-green-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Average Time</h3>
                  <p className="text-4xl font-bold">{calculateAverageTime(filteredMoves)}</p>
                </div>
                <div className="flex flex-col bg-yellow-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Completed Moves</h3>
                  <p className="text-4xl font-bold">{completedMoves.length}</p>
                </div>
                <div className="flex flex-col bg-purple-500 rounded-lg p-6 text-white shadow-lg min-h-[8rem] relative z-10">
                  <h3 className="text-2xl font-semibold mb-2">Success Rate</h3>
                  <p className="text-4xl font-bold">{successRate}%</p>
                </div>
              </>
            );
          })()}
        </div>

        {/* Detailed Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Move Types Distribution */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Move Types</h3>
            <div className="space-y-4">
              {['single', 'shuttle', 'triangle'].map(type => {
                const filteredMoves = getFilteredMoves();
                const count = filteredMoves.filter(move => move.move_details?.type === type).length;
                const percentage = filteredMoves.length > 0 ? (count / filteredMoves.length) * 100 : 0;
                return (
                  <div key={type} className="flex items-center">
                    <span className="text-xl capitalize w-24">{type}</span>
                    <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 rounded-full h-4"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xl font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Move Status Distribution */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Move Status</h3>
            <div className="space-y-4">
              {[
                { label: 'Requested', filter: move => !move.in_progress && !move.finish_time && !move.is_cancel },
                { label: 'In Progress', filter: move => move.in_progress && !move.finish_time && !move.is_cancel },
                { label: 'Completed', filter: move => move.finish_time && !move.is_cancel },
                { label: 'Cancelled', filter: move => move.is_cancel }
              ].map(({ label, filter }) => {
                const filteredMoves = getFilteredMoves();
                const count = filteredMoves.filter(filter).length;
                const percentage = filteredMoves.length > 0 ? (count / filteredMoves.length) * 100 : 0;
                return (
                  <div key={label} className="flex items-center">
                    <span className="text-xl w-32">{label}</span>
                    <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                      <div
                        className={`rounded-full h-4 ${
                          label === 'Completed' ? 'bg-green-600' :
                          label === 'In Progress' ? 'bg-yellow-600' :
                          label === 'Cancelled' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xl font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Location Analytics Section */}
      <div className="relative bg-white rounded-lg shadow p-8 mb-8 z-10">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-4xl font-bold">Location Analytics</h2>
            {selectedAccountId && (
              <p className="text-gray-600 text-xl mt-1">
                Showing data for {accounts.find(a => a.id === selectedAccountId)?.account_name || 'selected account'}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'daily' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="location-daily"
                type="radio"
                className="hidden"
                value="daily"
                checked={timeRange === 'daily'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Today</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'weekly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="location-weekly"
                type="radio"
                className="hidden"
                value="weekly"
                checked={timeRange === 'weekly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 7 Days</span>
            </label>
            <label className={`inline-flex items-center cursor-pointer px-4 py-2 rounded-lg ${
              timeRange === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}>
              <input
                id="location-monthly"
                type="radio"
                className="hidden"
                value="monthly"
                checked={timeRange === 'monthly'}
                onChange={(e) => setTimeRange(e.target.value)}
              />
              <span className="text-lg font-medium">Past 30 Days</span>
            </label>
          </div>
        </div>

        {/* Location Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[8rem] mb-8">
          {/* Average Time at Location */}
          <div className="bg-indigo-500 rounded-lg p-6 text-white shadow-lg">
            <h3 className="text-2xl font-semibold mb-2">Avg. Time at Locations</h3>
            <p className="text-4xl font-bold">
              {isLoadingLocationData ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                calculateAverageTimeAtLocations()
              )}
            </p>
          </div>

          {/* Most Active Locations */}
          <div className="bg-teal-500 rounded-lg p-6 text-white shadow-lg">
            <h3 className="text-2xl font-semibold mb-2">Most Active Location</h3>
            <p className="text-4xl font-bold">
              {isLoadingLocationData ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                findMostActiveLocation()
              )}
            </p>
          </div>

          {/* Idle Trailers */}
          <div className="bg-amber-500 rounded-lg p-6 text-white shadow-lg">
            <h3 className="text-2xl font-semibold mb-2">Idle Trailers</h3>
            <p className="text-4xl font-bold">
              {isLoadingLocationData ? (
                <span className="animate-pulse">Loading...</span>
              ) : (
                countIdleTrailers()
              )}
            </p>
          </div>
        </div>

        {/* Location Analytics Visualizations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Location Distribution */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Location Distribution</h3>
            {isLoadingLocationData ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-pulse text-2xl text-gray-500">Loading location data...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {getLocationDistribution().length === 0 ? (
                  <div className="text-center text-gray-500 italic">No location data available</div>
                ) : (
                  getLocationDistribution().map(([location, count]) => {
                    const totalTrailers = trailers.length;
                    const percentage = totalTrailers > 0 ? (count / totalTrailers) * 100 : 0;
                    return (
                      <div key={location} className="flex items-center">
                        <span className="text-xl w-32 truncate" title={location}>{location}</span>
                        <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-indigo-600 rounded-full h-4"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xl font-semibold">{count}</span>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Movement Frequency */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-2xl font-semibold mb-4">Popular Move Routes</h3>
            {isLoadingLocationData ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-pulse text-2xl text-gray-500">Loading route data...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const topRoutes = getPopularMoveRoutes();

                  if (topRoutes.length === 0) {
                    return <div className="text-center text-gray-500 italic">No completed moves data available</div>;
                  }

                  const maxCount = Math.max(...topRoutes.map(route => route[1]));

                  return topRoutes.map(([route, count]) => {
                    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div key={route} className="flex items-center">
                        <span className="text-xl w-40 truncate" title={route}>{route}</span>
                        <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-teal-600 rounded-full h-4"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xl font-semibold">{count}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedCompanyId && (
        <div className="grid grid-cols-1 gap-8">
          {/* Selected Account Details */}
          {selectedAccountId && (
            <div className="bg-white rounded-lg shadow p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-4xl font-bold text-blue-800">
                  {accounts.find(a => a.id === selectedAccountId)?.account_name}
                </h2>
                <div className="flex gap-4">
                  {['trailers', 'trucks', 'yards', 'buildings', 'users'].map(tab => (
                    <button id={tab}
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 rounded-lg text-xl font-semibold transform transition-all duration-200 ${
                        activeTab === tab
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md hover:scale-110'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content based on activeTab */}

              {/* Trailers Tab */}
              {activeTab === 'trailers' && (
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-3xl font-semibold">Trailers Overview</h3>
                    <div className="flex gap-4">
                      <button id='add-trailer-button'
                        onClick={() => history.push(`/trailers/new`)}
                        className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">+</span> Add New Trailer
                      </button>
                      <button id='bulk-upload-trailers-button'
                        onClick={() => history.push(`/trailers/bulk-upload`)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">↑</span> Bulk Upload Trailers
                      </button>
                      {isManagerOrAbove && (
                        <button
                          id='print-trailers-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            generateReport('trailers', accountTrailers, {
                              title: `${accountName} - Trailers Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Print</span> Trailers Report
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='excel-trailers-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            exportToExcel('trailers', accountTrailers, {
                              title: `${accountName} - Trailers Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-[#217346] hover:bg-[#185a36] text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Download</span> Trailers Excel Report
                        </button>
                      )}
                    </div>
                  </div>
                  {!selectedAccountId ? (
                    <div className="p-8 text-center bg-yellow-100 rounded-lg text-3xl">
                      Please select an account to view trailers
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Filter Section for Trailers - Always show filters */}
                      <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                          {/* Main filters container */}
                          <div className="flex flex-1 gap-6">
                            {/* Location Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Location</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="location-all"
                                    value="all"
                                    checked={selectedLocation === 'all'}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="location-road"
                                    value="road"
                                    checked={selectedLocation === 'road'}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Road</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="location-shop"
                                    value="shop"
                                    checked={selectedLocation === 'shop'}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Shop</span>
                                </label>
                                {generateLocationOptions().map((location, index) => (
                                  <label key={`location-${location}-${index}`} className="inline-flex items-center">
                                    <input
                                      type="radio"
                                      id={`location-${location}`}
                                      value={location}
                                      checked={selectedLocation === location}
                                      onChange={(e) => setSelectedLocation(e.target.value)}
                                      className="form-radio h-6 w-6 text-blue-600"
                                    />
                                    <span className="ml-2 text-lg capitalize">{location}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Status Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="status-all"
                                    value="all"
                                    checked={selectedTag === 'all'}
                                    onChange={(e) => setSelectedTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="status-red"
                                    value="red"
                                    checked={selectedTag === 'red'}
                                    onChange={(e) => setSelectedTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-red-600"
                                  />
                                  <span className="ml-2 text-lg text-red-600">Red Tagged</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="status-yellow"
                                    value="yellow"
                                    checked={selectedTag === 'yellow'}
                                    onChange={(e) => setSelectedTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-yellow-600"
                                  />
                                  <span className="ml-2 text-lg text-yellow-600">Yellow Tagged</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="status-available"
                                    value="available"
                                    checked={selectedTag === 'available'}
                                    onChange={(e) => setSelectedTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-green-600"
                                  />
                                  <span className="ml-2 text-lg text-green-600">Available</span>
                                </label>
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Load Type Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Load Type</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="loadType-all"
                                    value="all"
                                    checked={selectedLoadType === 'all'}
                                    onChange={(e) => setSelectedLoadType(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="loadType-loaded"
                                    value="loaded"
                                    checked={selectedLoadType === 'loaded'}
                                    onChange={(e) => setSelectedLoadType(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Loaded</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="loadType-empty"
                                    value="empty"
                                    checked={selectedLoadType === 'empty'}
                                    onChange={(e) => setSelectedLoadType(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Empty</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="loadType-trays"
                                    value="trays"
                                    checked={selectedLoadType === 'trays'}
                                    onChange={(e) => setSelectedLoadType(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">With Trays</span>
                                </label>
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Time at Location Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Time at Location</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="timeSort-asc"
                                    value="asc"
                                    checked={timeSort === 'asc'}
                                    onChange={(e) => setTimeSort(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Newest First</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="timeSort-desc"
                                    value="desc"
                                    checked={timeSort === 'desc'}
                                    onChange={(e) => setTimeSort(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Oldest First</span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Vertical Separator */}
                          <div className="w-px bg-gray-300 self-stretch"></div>

                          {/* Reset Button */}
                          <div className="flex items-center pl-6">
                            <button
                              onClick={() => {
                                setSelectedLocation('all');
                                setSelectedTag('all');
                                setSelectedLoadType('all');
                                setTimeSort('asc');
                              }}
                              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                              </svg>
                              Reset Filters
                            </button>
                          </div>
                        </div>
                      </div>

                          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] bg-blue-100 border-b border-gray-300 p-4 text-2xl font-semibold">
                            <div className="flex items-center justify-center">Location</div>
                            <div className="flex items-center justify-center">Trailer</div>
                            <div className="flex items-center justify-center">Type</div>
                            <div className="flex items-center justify-center">State - Plate</div>
                            <div className="flex items-center justify-center">Trailer Number</div>
                            <div className="flex items-center justify-center">Time at Location</div>
                            <div className="flex items-center justify-center">Load Number OR Trays</div>
                            <div className="flex items-center justify-center">Current User</div>
                            <div className="flex items-center justify-center">Tag Description</div>
                          </div>

                          {accountTrailers.map((trailer, index) => (
                            <div id='trailer-row'
                              className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] border-b border-gray-200 p-4 text-center
                                ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                                transition-all duration-200 cursor-pointer ${trailer?.red_tagged ? 'text-red-600' : ''}`}
                              key={trailer?.id}
                              onClick={() => history.push(`/trailers/${trailer?.id}`)}
                            >
                              <div className="flex items-center justify-center text-xl font-semibold">{trailer?.trailer_location || "Unknown"}</div>
                              <div className="flex items-center justify-center">
                                <img
                                  className="max-h-16"
                                  src={
                                    trailer?.is_loaded
                                      ? "/images/trailer-loadedYard.jpeg"
                                      : trailer?.is_tray
                                      ? "/images/trailer-traysFull.jpeg"
                                      : "/images/trailer-empty.jpeg"
                                  }
                                  alt={
                                    trailer?.is_loaded
                                      ? "Loaded Trailer"
                                      : trailer?.is_tray
                                      ? "Trailer with Trays"
                                      : "Empty Trailer"
                                  }
                                />
                              </div>
                              <div className="flex items-center justify-center text-xl font-semibold">{trailer?.type || "N/A"}</div>
                              <div className="flex items-center justify-center text-xl font-semibold">{`${trailer?.license_state || ""} - ${trailer?.license_plate || ""}`}</div>
                              <div className="flex items-center justify-center text-xl font-semibold">{trailer?.trailer_number || "N/A"}</div>
                              <div className="flex items-center justify-center text-xl font-semibold">{getCurrentLocationDuration(trailer?.id)}</div>
                              <div className="flex items-center justify-center text-xl font-semibold">
                                {trailer?.is_tray
                                  ? `${trailer?.tray_count} Tray${trailer?.tray_count !== 1 ? 's' : ''}`
                                  : trailer?.is_loaded
                                  ? trailer?.load_number
                                  : "Empty"}
                              </div>
                              <div className="flex items-center justify-center text-xl font-semibold">
                                {users.find(user => user.id === trailer?.user_id)?.alpha_code || "N/A"}
                              </div>
                              <div className="flex items-center justify-center min-w-[150px]">
                                <span
                                  className={`px-4 py-2 rounded-md ${getStatusColor(trailer)}
                                    break-words text-center w-full max-h-20 overflow-y-auto text-xl font-semibold`}
                                >
                                  {trailer?.tagged_description || ""}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                  )}
                </div>
              )}

              {/* Trucks Tab */}
              {activeTab === 'trucks' && (
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-3xl font-bold">Trucks Overview</h3>
                    <div className="flex gap-4">
                      <button id='add-truck-button'
                        onClick={() => history.push(`/trucks/new`)}
                        className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">+</span> Add New Truck
                      </button>
                      {isManagerOrAbove && (
                        <button
                          id='print-trucks-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            generateReport('trucks', accountTrucks, {
                              title: `${accountName} - Trucks Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Print</span> Trucks Report
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='excel-trucks-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            exportToExcel('trucks', accountTrucks, {
                              title: `${accountName} - Trucks Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-[#217346] hover:bg-[#185a36] text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Download</span> Trucks Excel Report
                        </button>
                      )}
                    </div>
                  </div>

                  {!selectedAccountId ? (
                    <div className="p-8 text-center bg-yellow-100 rounded-lg text-3xl">
                      Please select an account to view trucks
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Filter Section for Trucks - Always show filters */}
                      <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                          {/* Main filters container */}
                          <div className="flex flex-1 gap-6">
                            {/* Location Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Location</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-location-all"
                                    value="all"
                                    checked={selectedTruckLocation === 'all'}
                                    onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-location-road"
                                    value="road"
                                    checked={selectedTruckLocation === 'road'}
                                    onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Road</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-location-shop"
                                    value="shop"
                                    checked={selectedTruckLocation === 'shop'}
                                    onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">Shop</span>
                                </label>
                                {generateLocationOptions().map((location, index) => (
                                  <label key={`truck-location-${location}-${index}`} className="inline-flex items-center">
                                    <input
                                      type="radio"
                                      id={`truck-location-${location}`}
                                      value={location}
                                      checked={selectedTruckLocation === location}
                                      onChange={(e) => setSelectedTruckLocation(e.target.value)}
                                      className="form-radio h-6 w-6 text-blue-600"
                                    />
                                    <span className="ml-2 text-lg capitalize">{location}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Status Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-status-all"
                                    value="all"
                                    checked={selectedTruckTag === 'all'}
                                    onChange={(e) => setSelectedTruckTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-status-red"
                                    value="red"
                                    checked={selectedTruckTag === 'red'}
                                    onChange={(e) => setSelectedTruckTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-red-600"
                                  />
                                  <span className="ml-2 text-lg text-red-600">Red Tagged</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-status-yellow"
                                    value="yellow"
                                    checked={selectedTruckTag === 'yellow'}
                                    onChange={(e) => setSelectedTruckTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-yellow-600"
                                  />
                                  <span className="ml-2 text-lg text-yellow-600">Yellow Tagged</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-status-available"
                                    value="available"
                                    checked={selectedTruckTag === 'available'}
                                    onChange={(e) => setSelectedTruckTag(e.target.value)}
                                    className="form-radio h-6 w-6 text-green-600"
                                  />
                                  <span className="ml-2 text-lg text-green-600">Available</span>
                                </label>
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Assigned User Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Assigned To</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-assigned-all"
                                    value="all"
                                    checked={selectedTruckAssignedUser === 'all'}
                                    onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="truck-assigned-unassigned"
                                    value="unassigned"
                                    checked={selectedTruckAssignedUser === 'unassigned'}
                                    onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                    className="form-radio h-6 w-6 text-gray-600"
                                  />
                                  <span className="ml-2 text-lg">Unassigned</span>
                                </label>
                                {generateTruckAssignedUsersOptions().map((user, index) => (
                                  <label key={`truck-assigned-${user.id}-${index}`} className="inline-flex items-center">
                                    <input
                                      type="radio"
                                      id={`truck-assigned-${user.id}`}
                                      value={user.id.toString()}
                                      checked={selectedTruckAssignedUser === user.id.toString()}
                                      onChange={(e) => setSelectedTruckAssignedUser(e.target.value)}
                                      className="form-radio h-6 w-6 text-blue-600"
                                    />
                                    <span className="ml-2 text-lg">{user.alpha_code}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Vertical Separator */}
                          <div className="w-px bg-gray-300 self-stretch"></div>

                          {/* Reset Button */}
                          <div className="flex items-center pl-6">
                            <button
                              onClick={() => {
                                setSelectedTruckLocation('all');
                                setSelectedTruckTag('all');
                                setSelectedTruckAssignedUser('all');
                              }}
                              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                              </svg>
                              Reset Filters
                            </button>
                          </div>
                        </div>
                      </div>


                          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] bg-blue-100 border-b border-gray-300 p-4 text-2xl font-semibold">
                            <SortableHeader field="truck_location" label="Location" />
                            <div className="flex items-center justify-center">Truck</div>
                            <SortableHeader field="truck_number" label="Number" />
                            <SortableHeader field="type" label="Type" />
                            <SortableHeader field="make" label="Make" />
                            <SortableHeader field="model" label="Model" />
                            <div className="flex items-center justify-center">Time at Location</div>
                            <SortableHeader field="assigned_user_id" label="Assigned To" />
                            <SortableHeader field="status" label="Status" />
                          </div>
                          {accountTrucks.map((truck, index) => {
                            // Look up assigned user
                            const assignedUser = truck.assigned_user_id
                              ? users.find(user => user.id === truck.assigned_user_id)
                              : null;
                            return (
                              <div id='truck-row'
                                className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1.5fr] border-b border-gray-200 p-4 text-center
                                  ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                  hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                                  transition-all duration-200 cursor-pointer ${truck?.red_tagged ? 'text-red-600' : ''}`}
                                key={truck?.id}
                                onClick={() => history.push(`/trucks/${truck?.id}`)}
                              >
                                <div id='truck_location' className="flex items-center justify-center text-xl font-semibold">{truck?.truck_location || "Unknown"}</div>
                                <div className="flex items-center justify-center">
                                  <img
                                    className="max-h-16"
                                    src="/images/truck.png"
                                    alt="Truck"
                                  />
                                </div>
                                <div id='truck_number'className="flex items-center justify-center text-xl font-semibold">{truck?.truck_number || "N/A"}</div>
                                <div id='type' className="flex items-center justify-center text-xl font-semibold">{truck?.type || "N/A"}</div>
                                <div id='make' className="flex items-center justify-center text-xl font-semibold">{truck?.make || "N/A"}</div>
                                <div id='model'className="flex items-center justify-center text-xl font-semibold">{truck?.model || "N/A"}</div>
                                <div id='location_duration' className="flex items-center justify-center text-xl font-semibold">{getTruckLocationDuration(truck?.id)}</div>
                                <div id='alpha_code' className="flex items-center justify-center text-xl font-semibold">{assignedUser ? `${assignedUser.alpha_code}` : 'Not assigned'}</div>
                                <div id='status' className="flex items-center justify-center">
                                  <span className={`px-4 py-2 rounded-md ${getStatusColor(truck)} text-xl font-semibold`}>
                                    {getStatusText(truck)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </>
                  )}
                </div>
              )}

              {/* Yards Tab */}
              {activeTab === 'yards' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-3xl font-semibold">Yards Overview</h3>
                    <div className="flex gap-4">
                      {isManagerOrAbove && (
                        <button id='add-yard-button'
                          onClick={() => history.push(`/yards/new`)}
                          className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <span className="mr-2">+</span> Add New Yard
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='print-yards-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            const yardData = yards.filter(yard => yard.account_id === selectedAccountId);
                            generateReport('yards', yardData, {
                              title: `${accountName} - Yards Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Print</span> Yards Report
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='excel-yards-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            const yardData = yards.filter(yard => yard.account_id === selectedAccountId);
                            exportToExcel('yards', yardData, {
                              title: `${accountName} - Yards Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-[#217346] hover:bg-[#185a36] text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Download</span> Yards Excel Report
                        </button>
                      )}
                    </div>
                  </div>

                  {!selectedAccountId ? (
                    <div className="p-8 text-center bg-yellow-100 rounded-lg text-3xl">
                      Please select an account to view yards
                    </div>
                  ) : accountYards.length === 0 ? (
                    <div className="p-8 text-center bg-gray-50 rounded-lg text-3xl">
                      No yards found for this account.{' '}
                      <button
                        onClick={() => history.push(`/yards/new`)}
                        className="text-blue-600 hover:underline"
                      >
                        Add your first yard
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {accountYards.map(yard => (
                        <div id='yard-row'
                          key={yard.id}
                          onClick={() => history.push(`/yards/${yard.id}`)}
                          className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-xl
                            hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                            transition-all duration-200 cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-4">
                            <h4 className="text-3xl font-semibold">{yard.name}</h4>
                            <span className={`px-4 py-2 rounded-full text-xl ${
                              yard.parking_spots ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {yard.parking_spots ? 'Parking Spots Available' : 'No Parking Spots'}
                            </span>
                          </div>
                          <div className="space-y-3 text-2xl">
                            <p><span className="font-medium">Location:</span> {yard.parking_location}</p>
                            {yard.entry_log && (
                              <p className="text-gray-600">
                                Last Entry: {new Date(yard.entry_log.entry_time).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Buildings Tab */}
              {activeTab === 'buildings' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-3xl font-semibold">Buildings Overview</h3>
                    <div className="flex gap-4">
                      {isManagerOrAbove && (
                        <button id='add-building-button'
                          onClick={() => history.push(`/buildings/new`)}
                          className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          <span className="mr-2">+</span> Add New Building
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='print-buildings-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            const buildingData = buildings.filter(building => building.account_id === selectedAccountId);
                            generateReport('buildings', buildingData, {
                              title: `${accountName} - Buildings Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Print</span> Buildings Report
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='excel-buildings-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            const buildingData = buildings.filter(building => building.account_id === selectedAccountId);
                            exportToExcel('buildings', buildingData, {
                              title: `${accountName} - Buildings Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-[#217346] hover:bg-[#185a36] text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Download</span> Buildings Excel Report
                        </button>
                      )}
                    </div>
                  </div>

                  {!selectedAccountId ? (
                    <div className="p-8 text-center bg-yellow-100 rounded-lg text-3xl">
                      Please select an account to view buildings
                    </div>
                  ) : accountBuildings.length === 0 ? (
                    <div className="p-8 text-center bg-gray-50 rounded-lg text-3xl">
                      No buildings found for this account.{' '}
                      <button
                        onClick={() => history.push(`/buildings/new`)}
                        className="text-blue-600 hover:underline"
                      >
                        Add your first building
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {accountBuildings.map(building => (
                        <div id='building-row'
                          key={building.id}
                          onClick={() => history.push(`/buildings/${building.id}`)}
                          className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-xl
                            hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                            transition-all duration-200 cursor-pointer"
                        >
                          <div className="text-2xl">
                            <div className="mt-4">
                              <span className="font-medium">Building ID:</span>
                              <p className="text-3xl mt-1">{building.id}</p>
                            </div>
                            <div className="mt-4">
                              <span className="font-medium">Building Name:</span>
                              <p className="text-3xl mt-1">{building.building_name}</p>
                            </div>
                          </div>

                          <div className="text-2xl">
                            <div className="mt-4">
                              <span className="font-medium">Dock Doors:</span>
                              <p className="text-3xl mt-1">{building.building_door_number}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-3xl font-bold">Users Overview</h3>
                    <div className="flex gap-4">
                      <button id='add-user-button'
                        onClick={() => history.push(`/signup`)}
                        className="bg-green-600 hover:bg-green-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                      >
                        <span className="mr-2">+</span> Add New User
                      </button>
                      {isManagerOrAbove && (
                        <button
                          id='print-users-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            generateReport('users', accountUsers, {
                              title: `${accountName} - Users Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-purple-600 hover:bg-purple-700 text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Print</span> Users Report
                        </button>
                      )}
                      {isManagerOrAbove && (
                        <button
                          id='excel-users-report'
                          onClick={() => {
                            const accountName = accounts.find(a => a.id === selectedAccountId)?.account_name;
                            exportToExcel('users', accountUsers, {
                              title: `${accountName} - Users Report`,
                              subtitle: `Account: ${accountName}`
                            });
                          }}
                          className="bg-[#217346] hover:bg-[#185a36] text-white text-2xl px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
                        >
                          <span className="material-icons mr-2">Download</span> Users Excel Report
                        </button>
                      )}
                    </div>
                  </div>

                  {!selectedAccountId ? (
                    <div className="p-8 text-center bg-yellow-100 rounded-lg text-3xl">
                      Please select an account to view users
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Filter Section for Users - Always show filters */}
                      <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-6">
                          {/* Main filters container */}
                          <div className="flex flex-1 gap-6">
                            {/* Role Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Role</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="user-role-all"
                                    value="all"
                                    checked={selectedUserRole === 'all'}
                                    onChange={(e) => setSelectedUserRole(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                {generateUserRoleOptions().map((role, index) => (
                                  <label key={`user-role-${role.value}-${index}`} className="inline-flex items-center">
                                    <input
                                      type="radio"
                                      id={`user-role-${role.value}`}
                                      value={role.value}
                                      checked={selectedUserRole === role.value}
                                      onChange={(e) => setSelectedUserRole(e.target.value)}
                                      className="form-radio h-6 w-6 text-blue-600"
                                    />
                                    <span className="ml-2 text-lg">{role.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {/* Vertical Separator */}
                            <div className="w-px bg-gray-300 self-stretch"></div>

                            {/* Status Filter */}
                            <div className="flex-1">
                              <h3 className="text-2xl font-bold mb-2 text-gray-800">Status</h3>
                              <div className="flex flex-wrap gap-3">
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="user-status-all"
                                    value="all"
                                    checked={selectedUserStatus === 'all'}
                                    onChange={(e) => setSelectedUserStatus(e.target.value)}
                                    className="form-radio h-6 w-6 text-blue-600"
                                  />
                                  <span className="ml-2 text-lg">All</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="user-status-active"
                                    value="active"
                                    checked={selectedUserStatus === 'active'}
                                    onChange={(e) => setSelectedUserStatus(e.target.value)}
                                    className="form-radio h-6 w-6 text-green-600"
                                  />
                                  <span className="ml-2 text-lg text-green-600">Active</span>
                                </label>
                                <label className="inline-flex items-center">
                                  <input
                                    type="radio"
                                    id="user-status-inactive"
                                    value="inactive"
                                    checked={selectedUserStatus === 'inactive'}
                                    onChange={(e) => setSelectedUserStatus(e.target.value)}
                                    className="form-radio h-6 w-6 text-red-600"
                                  />
                                  <span className="ml-2 text-lg text-red-600">Inactive</span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Vertical Separator */}
                          <div className="w-px bg-gray-300 self-stretch"></div>

                          {/* Reset Button */}
                          <div className="flex items-center pl-6">
                            <button
                              onClick={() => {
                                setSelectedUserRole('all');
                                setSelectedUserStatus('all');
                              }}
                              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-lg font-semibold transition-all duration-200 flex items-center gap-2 h-12"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                              </svg>
                              Reset Filters
                            </button>
                          </div>
                        </div>
                      </div>

                          <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_2fr_1fr] bg-blue-100 border-b border-gray-300 p-4 text-2xl font-semibold">
                            <SortableHeader field="alpha_code" label="Alpha Code" />
                            <SortableHeader field="email" label="Email" />
                            <div className="flex items-center justify-center">Role</div>
                            <div className="flex items-center justify-center">Driver Number</div>
                            <div className="flex items-center justify-center">Account</div>
                            <SortableHeader field="is_active" label="Status" />
                          </div>
                          {accountUsers.map((user, index) => (
                            <div
                              key={user.id}
                              className={`grid grid-cols-[1fr_1.5fr_1fr_1fr_2fr_1fr] border-b border-gray-200 p-4 ${
                                !user.is_active ? 'opacity-75' : ''
                              } ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                              hover:shadow-xl hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                              transition-all duration-200 cursor-pointer`}
                              onClick={() => history.push(`/users/${user.id}`)}
                            >
                              <div className="flex items-center justify-center text-xl">{user.alpha_code || "N/A"}</div>
                              <div className="flex items-center justify-center text-xl">{user.email || "N/A"}</div>
                              <div className="flex items-center justify-center text-xl">
                                {user.is_super_manager ? "Super Manager" :
                                 user.is_account_manager ? "Account Manager" :
                                 user.is_manager ? "Manager" :
                                 user.is_admin ? "Admin" :
                                 user.is_security ? "Security" :
                                 user.is_mechanic ? "Mechanic" :
                                 user.is_road_driver ? "Road Driver" :
                                 user.is_yard_driver ? "Yard Driver" :
                                 "User"}
                              </div>
                              <div className="flex items-center justify-center text-xl">{user.driver_number || "N/A"}</div>
                              <div className="flex items-center justify-center text-xl">
                                {accounts.find(a => a.id === user.account_id)?.account_name || (user.is_admin && user.account_id === null ? "Global Admin" : "N/A")}
                              </div>
                              <div className="flex items-center justify-center">
                                <span className={`px-4 py-2 rounded-lg text-xl font-semibold ${
                                  user.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>

                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {showScrollButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-200 z-50"
          aria-label="Scroll to top"
        >
          <svg
            className="w-12 h-12"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </div>
  );
};

const QuickStat = ({ label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow">
      <div className="text-xl text-gray-600">{label}</div>
      <div className={`text-4xl font-bold mt-2 ${colorClasses[color]?.split(' ')[1]}`}>
        {value}
      </div>
    </div>
  );
};

export default AdminDashboard;
